import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { FeatureFlagsService } from '../../api/core/services';
import { ClientFeatureFlag, ClientFeatureFlagName } from '../../model/client-model/client-feature-flag';
import {
  apiBoardFeatureFlagsToBoardAndTenantClientFeatureFlags,
  apiFeatureFlagsToClientFeatureFlags,
} from '../backend-service/helpers/api-feature-flags-to-client-feature-flags';

export type BoardAndTenantClientFeatureFlags = {
  boardFeatureFlags: ClientFeatureFlag[];
  tenantFeatureFlags: ClientFeatureFlag[];
};

@Injectable({ providedIn: 'root' })
export class BackendFeatureFlagsService {
  private _featureFlagService = inject(FeatureFlagsService);

  boardFeatureFlags: ClientFeatureFlag[] | undefined;
  globalFeatureFlags: ClientFeatureFlag[] | undefined;
  tenantFeatureFlags: ClientFeatureFlag[] | undefined;

  private _cachedBoardAndTenantFlags: { [boardId: string]: BoardAndTenantClientFeatureFlags } = {};
  private _cachedBoardFlags: { [boardId: string]: ClientFeatureFlag[] } = {};
  private _cachedTenantFlags: { [tenantId: string]: ClientFeatureFlag[] } = {};

  getBoardAndTenantFeatureFlags(boardId: string): Observable<BoardAndTenantClientFeatureFlags> {
    const featureFlags$ = !!this._cachedBoardAndTenantFlags[boardId]
      ? of(this._cachedBoardAndTenantFlags[boardId])
      : this._featureFlagService
          .listBoardFeatureFlags({ boardId })
          .pipe(map(apiBoardFeatureFlagsToBoardAndTenantClientFeatureFlags));

    return featureFlags$.pipe(
      tap(({ boardFeatureFlags, tenantFeatureFlags }) => {
        this._cachedBoardAndTenantFlags[boardId] = { boardFeatureFlags, tenantFeatureFlags };
        this.boardFeatureFlags = boardFeatureFlags;
        this.tenantFeatureFlags = tenantFeatureFlags;
      }),
      catchError((error): Observable<BoardAndTenantClientFeatureFlags> => {
        console.error(`Could not load feature flags. All features will be hidden.`, error);
        return of({ boardFeatureFlags: [], tenantFeatureFlags: [] });
      })
    );
  }

  getBoardFeatureFlags(boardId: string): Observable<ClientFeatureFlag[]> {
    return !!this._cachedBoardFlags[boardId]
      ? of(this._cachedBoardFlags[boardId])
      : this._featureFlagService.listBoardFeatureFlags({ boardId }).pipe(
          map(({ boardFeatureFlags }) => apiFeatureFlagsToClientFeatureFlags({ featureFlags: boardFeatureFlags })),
          tap((boardFeatureFlags) => {
            this._cachedBoardFlags[boardId] = boardFeatureFlags;
            this.boardFeatureFlags = boardFeatureFlags;
          }),
          catchError((error) => {
            console.error(`Could not load feature flags. All features will be hidden.`, error);
            return of([]);
          })
        );
  }

  getGlobalFeatureFlags(): Observable<ClientFeatureFlag[]> {
    return !!this.globalFeatureFlags
      ? of(this.globalFeatureFlags)
      : this._featureFlagService.listFeatureFlags().pipe(
          map(apiFeatureFlagsToClientFeatureFlags),
          tap((gffs) => {
            this.globalFeatureFlags = gffs;
          }),
          catchError((error) => {
            console.error(`Could not load global feature flags.`, error);
            return of([]);
          })
        );
  }

  /**
   * NOTE: this should only be used for the admin route, since users in the editor will not have access to tenant feature flags if not explicitly provided
   * Tenant feature flags are automatically loaded for the editor route via the getBoardFeatureFlags method
   *
   * Gets the feature flags for a tenant.
   * @param tenantId The tenant ID to get the feature flags for.
   * @returns An observable of the feature flags for the tenant.
   */
  getTenantFeatureFlags(tenantId: string): Observable<ClientFeatureFlag[]> {
    return !!this._cachedTenantFlags[tenantId]
      ? of(this._cachedTenantFlags[tenantId])
      : this._featureFlagService.listTenantFeatureFlags({ tenantId }).pipe(
          map(apiFeatureFlagsToClientFeatureFlags),
          tap((tenantFeatureFlags) => {
            this._cachedTenantFlags[tenantId] = tenantFeatureFlags;
            this.tenantFeatureFlags = tenantFeatureFlags;
          }),
          catchError((error) => {
            console.error(`Could not load tenant feature flags.`, error);
            console.error(error);
            return of([]);
          })
        );
  }

  /**
   * Checks if a feature is enabled on a tenant or a board.
   * In templates you can also use FeatureFlagDirective.
   * @param flagName The feature flag name to check.
   * @returns True if the feature is enabled, false if not.
   */
  isEnabled(flagName: ClientFeatureFlagName): boolean {
    const matchingTenantFlag = this.tenantFeatureFlags?.some((tff) => tff.name === flagName);
    const matchingBoardFlag = !!this.boardFeatureFlags?.some((bff) => bff.name === flagName);

    return matchingTenantFlag || matchingBoardFlag;
  }

  loadFlagsForAdminRoute(): Observable<boolean> {
    return this.getGlobalFeatureFlags().pipe(map((gffs) => !!gffs));
  }

  loadFlagsForEditorOrPublicRoute(boardId: string): Observable<boolean> {
    return this.getBoardAndTenantFeatureFlags(boardId).pipe(
      map(({ boardFeatureFlags, tenantFeatureFlags }) => [boardFeatureFlags, tenantFeatureFlags].every((v) => !!v))
    );
  }

  updateBoardFeatureFlags(boardId: string, featureFlagIds: ClientFeatureFlag['id'][]): Observable<string> {
    featureFlagIds = this._persistDevZoneFlag(featureFlagIds, 'board');
    return this._featureFlagService.updateBoardFeatureFlags({ boardId, body: { featureFlagIds } }).pipe(
      tap(() => {
        const updateBoardFeatureFlags = featureFlagIds.map((id) =>
          this.globalFeatureFlags!.find((flag) => flag.id === id)
        );
        this._cachedBoardFlags[boardId] = <ClientFeatureFlag[]>updateBoardFeatureFlags;
        this.boardFeatureFlags = <ClientFeatureFlag[]>updateBoardFeatureFlags;
      })
    );
  }

  updateTenantFeatureFlags(tenantId: string, featureFlagIds: ClientFeatureFlag['id'][]): Observable<string> {
    featureFlagIds = this._persistDevZoneFlag(featureFlagIds, 'tenant');
    return this._featureFlagService.updateTenantFeatureFlags({ tenantId, body: { featureFlagIds } }).pipe(
      tap(() => {
        const updateTenantFeatureFlags = featureFlagIds.map((id) =>
          this.globalFeatureFlags!.find((flag) => flag.id === id)
        );
        this._cachedTenantFlags[tenantId] = <ClientFeatureFlag[]>updateTenantFeatureFlags;
        this.tenantFeatureFlags = <ClientFeatureFlag[]>updateTenantFeatureFlags;
      })
    );
  }

  private _persistDevZoneFlag(
    featureFlagIds: ClientFeatureFlag['id'][],
    updateType: 'board' | 'tenant'
  ): ClientFeatureFlag['id'][] {
    const devZoneFlag = this.globalFeatureFlags!.find((flag) => flag.name === ClientFeatureFlagName.DevZone);
    if (!devZoneFlag) {
      return featureFlagIds;
    }
    const setPreviously = (
      updateType === 'board' ? this.boardFeatureFlags : updateType === 'tenant' ? this.tenantFeatureFlags : []
    )!.some((flag) => flag.name === ClientFeatureFlagName.DevZone);
    const setNow = featureFlagIds.includes(devZoneFlag.id);
    if (setPreviously && !setNow) {
      return [...featureFlagIds, devZoneFlag.id];
    }
    return featureFlagIds;
  }
}
